/**
 * Refund or cancellation request (BAR-3069): what care ops asks for from the patient timeline.
 * The request lands in Finance's queue in the payments backend (BAR-3033); a self-pay request
 * waits for Finance to approve it, an employer or insurance cancellation (no money back) is final.
 * The reasons are closed lists, one per case, from `timeline-shared.mdx`.
 */

export type RefundRequestCase = 'cancel' | 'keep' | 'payor';

export const REFUND_REQUEST_REASONS: Record<RefundRequestCase, readonly string[]> = {
  cancel: [
    'Patient changed their mind',
    'Cost or financing',
    'Medically ineligible',
    'Did not clear pre-procedure requirements',
    'Health changed',
    'Chose another provider',
    'Moving or relocating',
    'Timing or scheduling',
    'Switching to employer or insurance coverage',
    'Other',
  ],
  keep: [
    'Price adjustment',
    'Promotion applied after payment',
    'Duplicate payment',
    'Overpayment',
    'Goodwill',
    'Other',
  ],
  payor: [
    'Patient changed their mind',
    'Medically ineligible',
    'Did not clear pre-procedure requirements',
    'Health changed',
    'Chose another provider',
    'Moving or relocating',
    'Timing or scheduling',
    'Lost the coverage',
    'Approval withdrawn by the payor',
    'Other',
  ],
};

/** Dollars to cents and back, so a typed amount never carries float noise to the backend. */
function roundToCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/**
 * A self-pay refund amount the request can carry: positive, at most what was paid. Undefined
 * for anything else.
 */
export function validRefundAmount(amount: number | string | undefined, paid: number): number | undefined {
  const value = typeof amount === 'string' ? Number(amount.replace(/[$,\s]/g, '')) : amount;
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const rounded = roundToCents(value);
  return rounded > 0 && rounded <= roundToCents(paid) ? rounded : undefined;
}

/** The parts of a refund request (payments backend, BAR-3033) that still owe the patient money. */
export interface OpenRefundRequest {
  amountRequested: number;
  amountApproved: number | null;
  /** Set once the refund that left is linked: it then counts in what was paid, not here. */
  linkedRefund: unknown;
}

/**
 * What a new self-pay request may still ask for: what the order took (already net of refunds that
 * left), less every request still waiting to leave, at Finance's approved amount once there is one.
 * Without this, a $500 Keep it request pending on $9,300 paid would let a Cancel it ask for $9,300
 * more.
 */
export function refundableAmount(paid: number, requests: readonly OpenRefundRequest[]): number {
  const open = requests
    .filter((request) => !request.linkedRefund)
    .reduce((sum, request) => sum + (request.amountApproved ?? request.amountRequested), 0);
  return Math.max(0, roundToCents(paid - open));
}
